// Compact salon → card-view mapper.
//
// SearchPage renders dozens of salon cards at once; each one needs a
// flat shape (image, rating, price-range, top 2 services) instead of
// the full nested response the API returns. Keeping this transform
// out of the component makes it testable and means swapping the
// backend response shape is one-file work.
//
// Pure. No React, no DOM, no network.

import { resolveActivePrice } from './servicePrice';
import { cld } from './cloudinary';

// Card/list/map surfaces render the cover at most ~card-width; 800px (retina)
// is plenty and keeps the iOS decoder from chewing on full-res originals.
const CARD_W = 800;

export function mapApiSalon(s) {
  const topServices = (s.services || []).slice(0, 2).map((svc) => ({
    name: svc.name,
    price: resolveActivePrice(svc),
    duration: svc.duration || 30,
  }));
  const prices = (s.services || [])
    .map((svc) => resolveActivePrice(svc))
    .filter((p) => !Number.isNaN(p) && p > 0);
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;
  return {
    id: s.id,
    slug: s.slug,
    name: s.name,
    type: s.type,
    image: cld(s.coverImage || s.coverPhoto || (s.gallery && s.gallery[0]) || s.logo || null, CARD_W),
    // De-duped photo list (cover first, then gallery) for the map-card carousel.
    // Dedupe on the original URL, store the sized URL.
    photos: (() => {
      const out = [];
      const seen = new Set();
      const push = (p) => { if (p && !seen.has(p)) { seen.add(p); out.push(cld(p, CARD_W)); } };
      push(s.coverImage);
      push(s.coverPhoto);
      (Array.isArray(s.gallery) ? s.gallery : []).forEach(push);
      push(s.logo);
      return out;
    })(),
    rating: Number(s.avgRating || 0).toFixed(1),
    // Raw (un-rounded) rating for the relevance/Bayesian ranking — `rating`
    // above is a 1-decimal display string and loses precision.
    ratingValue: Number(s.avgRating || 0),
    latitude: s.latitude || null,
    longitude: s.longitude || null,
    reviewCount: Number(s.totalReviews || 0),
    city: s.city || '',
    address: s.address || '',
    topServices,
    priceRange: { min, max },
    // Signals consumed by salonRanking.js (the "Recomandate" relevance sort):
    description: s.description || '',
    // The list caps attached services at 5, so this reliably distinguishes
    // "has >= 5 services" for the completeness score.
    serviceCount: Array.isArray(s.services) ? s.services.length : 0,
    // „Salon fondator" — permanent trust badge for the launch cohort.
    isFounder: !!s.isFoundingPartner,
    // A real cover/gallery photo (not just a logo fallback).
    hasCover: !!(s.coverImage || s.coverPhoto || (Array.isArray(s.gallery) && s.gallery[0])),
    createdAt: s.createdAt || null,
  };
}
